<template>
    <div>
        <div v-if="selectedSpec">
            <slot :loading="loading" />
            <nav
                v-if="editMode">
                <div
                    class="toolbar is-flex is-flex-wrap-wrap is-align-items-center mb-2 is-gap-10">
                    <button
                        class="button is-info"
                        @click="wireCopyActive = true">
                        <span class="icon is-small">
                            <i
                                class="mdi mdi-content-copy"
                                aria-hidden="true" />
                        </span>
                        <span>
                            Copy from spec
                        </span>
                    </button>
                    <button
                        class="button is-info"
                        :disabled="wireCodes.length > 0"
                        @click="generateCodes">
                        <span class="icon is-small">
                            <i
                                class="mdi mdi-auto-fix"
                                aria-hidden="true" />
                        </span>
                        <span>
                            Generate group-wire codes
                        </span>
                    </button>
                    <button
                        class="button is-info"
                        style="margin-right: auto"
                        :disabled="!enableCopyColor"
                        @click="copyColors">
                        <span class="icon is-small">
                            <i
                                class="mdi mdi-content-copy"
                                aria-hidden="true" />
                        </span>
                        <span>
                            Copy colors from first group to rest
                        </span>
                    </button>
                    <button
                        class="button is-info"
                        @click="activateModal">
                        <span class="icon is-small">
                            <i
                                class="mdi mdi-plus-circle-outline"
                                aria-hidden="true" />
                        </span>
                        <span>
                            New row
                        </span>
                    </button>
                </div>
            </nav>
            <div
                class="mt-1">
                <b-table
                    :loading="loading"
                    :hoverable="true"
                    :bordered="true"
                    :data="wireCodes"
                    :class="wireCodes.length > 8 ? 'outlined-box sticky-headers site-sticky-header long-list' : 'outlined-box'">
                    <b-table-column
                        v-slot="slotProps"
                        width="3ex">
                        <b-icon
                            v-if="slotProps.row.meta.edited && !slotProps.row.meta.isNew"
                            type="is-danger"
                            class="mdi mdi-pencil" />
                        <b-icon
                            v-if="slotProps.row.meta.isNew"
                            type="is-danger"
                            class="mdi mdi-plus-circle" />
                    </b-table-column>
                    <b-table-column
                        v-slot="slotProps"
                        field="WireGroup"
                        label="Group No"
                        width="60">
                        {{ slotProps.row.values.WireGroup }}
                    </b-table-column>
                    <b-table-column
                        v-slot="slotProps"
                        field="WireNo"
                        label="Wire"
                        width="30">
                        {{ slotProps.row.values.WireNo }}
                    </b-table-column>
                    <b-table-column
                        v-slot="slotProps"
                        field="WireColour"
                        label="Colour code">
                        <code-ref-selector
                            class="min-width-wire-colour"
                            :position="dropdownPosition(slotProps)"
                            :complex="false"
                            :disabled="!editMode"
                            :selected="slotProps.row.values.WireColour"
                            :code-refs="originalCodes(libNames.cableWireColour).map(c => c.values)"
                            @ref-selected="handleChange(slotProps.row, 'WireColour', $event)" />
                    </b-table-column>
                    <b-table-column
                        v-slot="slotProps"
                        field="isValid"
                        label="Valid"
                        width="10">
                        <bool-selector
                            v-if="editMode"
                            :value="slotProps.row.values.isValid ? 'True' : 'False'"
                            :visible="editMode"
                            @bool-select="handleChange(slotProps.row, 'isValid', !slotProps.row.values.isValid)" />
                        <bool-element
                            v-else
                            :size="'is-medium'"
                            :custom-size="'mdi-18px'"
                            :value="slotProps.row.values.isValid" />
                    </b-table-column>

                    <template #empty>
                        <section class="section">
                            <div class="content has-text-grey has-text-centered">
                                <p
                                    v-if="!loading">
                                    No wire color codes exists for this specification
                                </p>
                            </div>
                        </section>
                    </template>
                </b-table>
            </div>
            <b-modal
                v-model="wireCopyActive"
                has-modal-card>
                <wire-colour-copy
                    v-if="selectedSpec && selectedSpec.id"
                    :spec="selectedSpec.id"
                    :current-screen="selectedCode(libNames.cableCode).values.Screen"
                    @copy="editDone"
                    @close="onCloseWireColourCopy" />
            </b-modal>
            <b-modal
                v-if="template"
                v-model="modalActive"
                has-modal-card>
                <div
                    class="modal-card"
                    style="width: auto">
                    <header class="modal-card-head">
                        <p class="modal-card-title">
                            Add new wire color relation
                        </p>
                    </header>
                    <section
                        class="modal-card-body"
                        style="padding-bottom: 250px;">
                        <b-field
                            horizontal
                            label="Relation is valid:">
                            <bool-selector
                                :value="template.values.isValid"
                                @bool-select="template.values.isValid = $event" />
                        </b-field>
                        <b-field
                            v-if="originalCodes(libNames.cableWireGroup).length"
                            horizontal
                            label="Group number">
                            <code-ref-selector
                                :code-refs="originalCodes(libNames.cableWireGroup).map(c => c.values)"
                                :required="true"
                                :placeholder-override="''"
                                :complex="false"
                                :sort-func="(a, b) => a.name.localeCompare(b.name, undefined, {numeric: true})"
                                @ref-selected="template.values.WireGroup = $event" />
                        </b-field>
                        <b-field
                            horizontal
                            label="Wire number">
                            <b-input
                                v-model="template.values.WireNo"
                                type="number"
                                placeholder="Please enter a number"
                                min="1"
                                :required="true" />
                        </b-field>
                    </section>
                    <footer class="modal-card-foot">
                        <button
                            class="button"
                            type="button"
                            @click="modalActive=false">
                            Cancel
                        </button>
                        <button
                            class="button is-primary"
                            :disabled="!canSaveColorRelation"
                            @click="addNew">
                            Create
                        </button>
                    </footer>
                </div>
            </b-modal>
        </div>
        <p
            v-else
            class="title is-6 has-text-centered has-margin-bottom">
            A new specification must be released before wire color codes can be created
        </p>
    </div>
</template>

<script>
    import { useCodeEditStore } from '@/stores/codeEditStore.ts';
    import _ from 'lodash';
    import { mapActions, mapState, mapStores } from 'pinia';
    import BoolElement from '../../../shared/components/BoolElement.vue';
    import BoolSelector from '../../../shared/components/BoolSelector.vue';
    import CodeRefSelector from '../../../shared/components/CodeRefSelector.vue';
    import { getCableSpecWire, libNames } from '../../../shared/helpers/cableEditHelpers';
    import { showMixin } from '../../../shared/mixins/showMixin';
    import WireColourCopy from './WireColourCopy.vue';

    export default {
        components: {
            CodeRefSelector,
            BoolSelector,
            WireColourCopy,
            BoolElement
        },
        mixins: [
            showMixin
        ],
        props: {
            selectedSpec: {
                required: false,
                type: Object,
                default: null
            },
            editMode: {
                type: Boolean,
                required: true
            }
        },
        emits: [
            'edited',
            'done'
        ],
        data() {
            return {
                libNames,
                wireCodesCopy: [],
                template: null,
                wireGroupCopy: [],
                modalActive: false,
                wireCopyActive: false,
                originalWireColors: [],
                loading: false,
                loadedWireCodes: new Set(),
                filter: c => c.values.CableSpec === this.codeEditStore.selectedCodes[libNames.cableSpec]
            };
        },
        computed: {
            ...mapStores(useCodeEditStore),
            ...mapState(useCodeEditStore, ['scope']),
            wireCodes() {
                if (this.editMode)
                    return this.wireCodesCopy;
                else
                    return [...this.filteredCodes(libNames.cableSpecWire)].sort(this.sortWires);
            },
            hasChanges() {
                return this.wireCodes.some(wc => wc.meta.isNew || wc.meta.edited);
            },
            enableCopyColor() {
                if (this.selectedSpec.groups !== '1' && this.wireCodesCopy.length > 0)
                    return this.wireCodesCopy.filter(c => c.values.WireGroup === '001').every(c => c.values.WireColour !== '');
                else
                    return false;
            },
            canSaveColorRelation() {
                const wireNo = parseInt(this.template?.values.WireNo) === parseFloat(this.template?.values.WireNo);

                return this.template?.values.WireGroup && wireNo;
            }
        },
        watch: {
            editMode() {
                if (this.editMode) {
                    this.originalWireColors = JSON.parse(JSON.stringify(this.originalCodes(libNames.cableSpecWire))).filter(this.filter).sort(this.sortWires);
                    this.wireCodesCopy = JSON.parse(JSON.stringify(this.filteredCodes(libNames.cableSpecWire))).sort(this.sortWires);
                }
            },
            hasChanges(value) {
                this.$emit('edited', value);
            },
            async selectedSpec(spec) {
                await this.loadWireColorsForSpec(spec);
            }
        },
        mounted() {
            if (this.editMode)
                this.wireCodesCopy = JSON.parse(JSON.stringify(this.filteredCodes(libNames.cableSpecWire))).sort(this.sortWires);

            this.originalWireColors = JSON.parse(JSON.stringify(this.originalCodes(libNames.cableSpecWire))).filter(this.filter).sort(this.sortWires);
        },
        methods: {
            ...mapActions(useCodeEditStore, ['filteredCodes', 'originalCodes', 'selectedCode', 'isFullyLoaded']),
            dropdownPosition(tblSlotProps) {
                // The first 5 elements always have their dropdown expanded downwards, and the last 5 upwards.
                // This is to prevent having to scroll the wire-colour-table to view the options.
                if (tblSlotProps.index < 5) {
                    return 'bottom';
                }
                if ((this.wireCodes.length - tblSlotProps.index) <= 5) {
                    return 'top';
                }
                return 'auto';
            },
            sortWires(a, b) {
                const g = parseInt(a.values.WireGroup, 10) - parseInt(b.values.WireGroup, 10);
                if (g === 0)
                    return parseInt(a.values.WireNo, 10) - parseInt(b.values.WireNo, 10);
                return g;
            },
            createSpecWireName(code) {
                return code.values.WireGroup + '-' + code.values.WireNo + '-' + code.values.WireColour;
            },
            handleChange(code, field, change) {
                code.values[field] = change;
                this.wireCodesCopy = this.wireCodesCopy.slice(); // force update on array
                if (field === 'WireColour')
                    code.values.name = this.createSpecWireName(code);

                code.meta.edited = this.checkEdited(code);
                this.$emit('edited');
            },
            addNew() {
                this.modalActive = false;
                if (this.wireCodesCopy.some(c => c.values.WireGroup + c.values.WireNo === this.template.values.WireGroup + this.template.values.WireNo)) {
                    this.showError('Cannot create code with group + wire number as it exists');
                } else {
                    this.template.values.name = this.createSpecWireName(this.template);
                    this.template.meta.isNew = true;
                    if (this.template.values.WireNo === '0')
                        this.template.values.WireColour = 'S';

                    this.wireCodesCopy = [...this.wireCodesCopy, this.template];
                    this.$emit('edited');
                }
                this.initTemplate();
            },
            addNewWireGroup(group) {
                if (!this.wireGroupCopy.some(wg => wg.values.name === group)) {
                    const temp = this.getWireGroupTemplate();
                    temp.meta.isNew = true;
                    temp.values.name = group;
                    this.wireGroupCopy.push(temp);
                }
            },
            editDone() {
                this.wireCodesCopy.forEach(wc => {
                    if (wc.meta.isNew) {
                        const cswChanges = this.codeEditStore.changes[libNames.cableSpecWire];
                        if (cswChanges && cswChanges.find(c => c.values.identity === wc.values.identity))
                            this.codeEditStore.removeChange(libNames.cableSpecWire, wc.values.identity);
                        this.codeEditStore.changeCode(libNames.cableSpecWire, wc, 'create');
                    } else {
                        this.codeEditStore.changeCode(libNames.cableSpecWire, wc, 'update');
                    }
                });

                this.wireGroupCopy.forEach(wgc => {
                    const changes = this.codeEditStore.changes[libNames.cableWireGroup];
                    const existingWireGroup = this.codeEditStore.originalCodes(libNames.cableWireGroup);
                    if (changes && changes.find(c => c.values.identity === wgc.values.identity)) {
                        this.codeEditStore.removeChange(libNames.cableWireGroup, wgc.values.identity);
                    } else if (!existingWireGroup.some(ewg => ewg.values.name === wgc.values.name)) {
                        this.codeEditStore.changeCode(libNames.cableWireGroup, wgc, 'create');
                    }
                });

                this.$emit('done');
            },
            activateModal() {
                this.initTemplate();
                this.modalActive = true;
            },
            onCloseWireColourCopy() {
                this.wireCopyActive = false;
            },
            generateCodes() {
                this.initTemplate();

                const screen = this.selectedCode(libNames.cableCode).values.Screen.toLowerCase();
                if (screen === 'c') {
                    this.template.values.WireNo = '0';
                    this.template.values.WireGroup = '000';
                    this.addNewWireGroup('000');
                    this.addNew();
                }

                const iMax = parseInt(this.selectedCode(libNames.cableSpec).values.NumberOfGroups, 10);
                const jMax = parseInt(this.selectedCode(libNames.cableSpec).values.NumberOfWires, 10);

                for (let i = 1; i <= iMax; i++) {
                    let j;
                    screen === 'i' ? j = 0 : j = 1;
                    for (j; j <= jMax; j++) {
                        let group = i.toString(10);
                        group = group.padStart(3, '0');
                        this.addNewWireGroup(group);
                        this.template.values.WireGroup = group;
                        this.template.values.WireNo = j.toString(10);
                        this.addNew();
                    }
                }
            },
            initTemplate() {
                this.template = JSON.parse(JSON.stringify(this.codeEditStore.templates[libNames.cableSpecWire]));
                this.template.values.CableSpec = this.selectedSpec.id;
                this.template.values.isValid = 'True';
            },
            async loadWireColorsForSpec(spec) {
                if (!spec
                    || this.isFullyLoaded(libNames.cableSpecWire)
                    || this.loadedWireCodes.has(spec.id))
                    return;

                this.loading = true;
                const cableSpecWire = await getCableSpecWire(this, spec.id, this.scope);
                await this.codeEditStore.addCodes(libNames.cableSpecWire, cableSpecWire);
                this.loading = false;

                this.loadedWireCodes.add(spec.id);
            },
            getWireGroupTemplate() {
                const wireGroupTemplate = JSON.parse(JSON.stringify(this.codeEditStore.templates[libNames.cableWireGroup]));
                wireGroupTemplate.values.isValid = 'True';

                return wireGroupTemplate;
            },
            eraseChanges() {
                const cswChanges = this.codeEditStore.changes[libNames.cableSpecWire];
                if (cswChanges && cswChanges.length) {
                    this.wireCodesCopy.forEach(wc => {
                        if ((wc.meta.edited || wc.meta.isNew)
                            && cswChanges.find(c => c.values.identity === wc.values.identity))
                            this.codeEditStore.removeChange(libNames.cableSpecWire, wc.values.identity);
                    });
                }

                this.wireCodesCopy = JSON.parse(JSON.stringify(this.filteredCodes(libNames.cableSpecWire))).sort(this.sortWires);
            },
            copyColors() {
                const groups = parseInt(this.selectedSpec.groups, 10);
                let wires = parseInt(this.selectedSpec.wires, 10);
                wires += this.selectedCode(libNames.cableCode).values.Screen.toLowerCase() === 'i' ? 1 : 0;

                let colors;

                for (let i = 1; i <= groups; i++) {
                    const g = i.toString(10).padStart(3, '0');
                    const currentGroup = this.wireCodesCopy.filter(c => c.values.WireGroup === g);
                    if (i === 1) {
                        colors = currentGroup.map(c => c.values.WireColour);
                    } else if (currentGroup.length === wires) {
                        for (let j = 0; j < wires; j++) {
                            if (currentGroup[j].values.WireColour !== colors[j]) {
                                currentGroup[j].values.WireColour = colors[j];
                                currentGroup[j].meta.edited = true;
                            }
                        }
                    }
                }
                this.$emit('edited');
            },
            checkEdited(row) {
                const originalRow = this.originalWireColors.find(owc => owc.identity === row.identity);
                return !_.isEqual(row.values, originalRow?.values);
            }
        }
    };
</script>
<style scoped>

.long-list {
    max-height: 66vh;
    overflow-x: hidden;
}

.toolbar {
    row-gap: 0.5em;
}
.modal-card-foot {
    justify-content: flex-end;
}
.min-width-wire-colour {
    min-width: 100px;
}
</style>
